import { isApiMenuSource } from "@/config/menuSource";
// Menu mock data. Localized text objects {tr,en,de,fr,ru,ar} — every supported
// UI language must be present (enforced by src/__tests__/i18n.test.js).
// Structure mirrors a future FudiTap menu API.
//
// The exported `categories` / `products` are the LIVE menu store. They hold this
// demo data only when the menu source is "mock" (development/test). With the
// API source (always in production) they start empty and are filled by
// installMenu() from the FudiTap guest menu API; see src/lib/guestMenu.js.
//
// Modifier options may carry:
//   priceDelta        — added to the unit price
//   nutritionDelta    — signed kcal/protein/carbs/fat change (negative for removals)
//   nutritionMultiplier (size options only)
//   allergensAdd / allergensRemove — allergen ids added / removed by the option.
//     Only use allergensRemove when the removed ingredient is the sole source.
// Nutrition values are approximate demo values, not medical/dietary advice.

const mockCategories = [
  { id: "cat_breakfast", slug: "kahvalti", icon: "EggFried", name: { tr: "Kahvaltı", en: "Breakfast", de: "Frühstück", fr: "Petit-déjeuner", ru: "Завтрак", ar: "الإفطار" } },
  { id: "cat_burgers", slug: "burgerler", icon: "Beef", name: { tr: "Burgerler", en: "Burgers", de: "Burger", fr: "Burgers", ru: "Бургеры", ar: "البرجر" } },
  { id: "cat_pizza", slug: "pizzalar", icon: "Pizza", name: { tr: "Pizzalar", en: "Pizzas", de: "Pizzen", fr: "Pizzas", ru: "Пицца", ar: "البيتزا" } },
  { id: "cat_pasta", slug: "makarnalar", icon: "Wheat", name: { tr: "Makarnalar", en: "Pasta", de: "Pasta", fr: "Pâtes", ru: "Паста", ar: "المعكرونة" } },
  { id: "cat_salads", slug: "salatalar", icon: "Salad", name: { tr: "Salatalar", en: "Salads", de: "Salate", fr: "Salades", ru: "Салаты", ar: "السلطات" } },
  { id: "cat_drinks", slug: "icecekler", icon: "CupSoda", name: { tr: "İçecekler", en: "Drinks", de: "Getränke", fr: "Boissons", ru: "Напитки", ar: "المشروبات" } },
  { id: "cat_desserts", slug: "tatlilar", icon: "CakeSlice", name: { tr: "Tatlılar", en: "Desserts", de: "Nachtisch", fr: "Desserts", ru: "Десерты", ar: "الحلويات" } },
];

// Reusable modifier groups
const sauceGroup = {
  id: "mg_sauce",
  required: false,
  min: 0,
  max: 3,
  name: { tr: "Sos Seçimi", en: "Sauce Selection", de: "Soßenauswahl", fr: "Choix de sauce", ru: "Выбор соуса", ar: "اختيار الصلصة" },
  options: [
    { id: "s_bbq", name: { tr: "BBQ Sos", en: "BBQ Sauce", de: "BBQ-Soße", fr: "Sauce barbecue", ru: "Соус барбекю", ar: "صلصة باربكيو" }, priceDelta: 15, nutritionDelta: { calories: 35, protein: 0, carbs: 7, fat: 1 } },
    { id: "s_ranch", name: { tr: "Ranch Sos", en: "Ranch Sauce", de: "Ranch-Soße", fr: "Sauce ranch", ru: "Соус ранч", ar: "صلصة رانش" }, priceDelta: 15, nutritionDelta: { calories: 60, protein: 1, carbs: 2, fat: 6 }, allergensAdd: ["dairy", "egg"] },
    { id: "s_garlic", name: { tr: "Sarımsaklı Sos", en: "Garlic Sauce", de: "Knoblauchsoße", fr: "Sauce à l'ail", ru: "Чесночный соус", ar: "صلصة الثوم" }, priceDelta: 15, nutritionDelta: { calories: 55, protein: 1, carbs: 2, fat: 5 }, allergensAdd: ["egg"] },
    { id: "s_chili", name: { tr: "Acı Biber Sos", en: "Chili Sauce", de: "Chilisoße", fr: "Sauce pimentée", ru: "Соус чили", ar: "صلصة الفلفل الحار" }, priceDelta: 15, nutritionDelta: { calories: 25, protein: 0, carbs: 5, fat: 0 } },
  ],
};

const sizeGroup = (deltas = [0, 25, 45]) => ({
  id: "mg_size",
  required: true,
  min: 1,
  max: 1,
  name: { tr: "Boyut", en: "Size", de: "Größe", fr: "Taille", ru: "Размер", ar: "الحجم" },
  options: [
    { id: "sz_s", name: { tr: "Küçük", en: "Small", de: "Klein", fr: "Petit", ru: "Маленький", ar: "صغير" }, priceDelta: deltas[0], default: true, nutritionMultiplier: 0.82 },
    { id: "sz_m", name: { tr: "Orta", en: "Medium", de: "Mittel", fr: "Moyen", ru: "Средний", ar: "وسط" }, priceDelta: deltas[1], nutritionMultiplier: 1 },
    { id: "sz_l", name: { tr: "Büyük", en: "Large", de: "Groß", fr: "Grand", ru: "Большой", ar: "كبير" }, priceDelta: deltas[2], nutritionMultiplier: 1.25 },
  ],
});

const burgerExtras = {
  id: "mg_extras",
  required: false,
  min: 0,
  max: 5,
  name: { tr: "Ekstralar", en: "Extras", de: "Extras", fr: "Suppléments", ru: "Добавки", ar: "إضافات" },
  options: [
    { id: "e_cheese", name: { tr: "Ekstra Peynir", en: "Extra Cheese", de: "Extra Käse", fr: "Supplément fromage", ru: "Дополнительный сыр", ar: "جبن إضافي" }, priceDelta: 25, nutritionDelta: { calories: 90, protein: 6, carbs: 1, fat: 7 }, allergensAdd: ["dairy"] },
    { id: "e_meat", name: { tr: "Ekstra Köfte", en: "Extra Patty", de: "Extra Patty", fr: "Steak supplémentaire", ru: "Дополнительная котлета", ar: "قطعة لحم إضافية" }, priceDelta: 55, nutritionDelta: { calories: 150, protein: 14, carbs: 0, fat: 10 } },
    { id: "e_bacon", name: { tr: "Dana Bacon", en: "Beef Bacon", de: "Rinderspeck", fr: "Bacon de bœuf", ru: "Говяжий бекон", ar: "لحم بقري مقدد" }, priceDelta: 40, nutritionDelta: { calories: 80, protein: 6, carbs: 0, fat: 6 } },
    { id: "e_egg", name: { tr: "Yumurta", en: "Fried Egg", de: "Spiegelei", fr: "Œuf au plat", ru: "Яичница", ar: "بيضة مقلية" }, priceDelta: 20, nutritionDelta: { calories: 90, protein: 6, carbs: 1, fat: 7 }, allergensAdd: ["egg"] },
    { id: "e_onion", name: { tr: "Karamelize Soğan", en: "Caramelized Onion", de: "Karamellzwiebel", fr: "Oignons caramélisés", ru: "Карамелизированный лук", ar: "بصل مكرمل" }, priceDelta: 18, nutritionDelta: { calories: 20, protein: 0, carbs: 4, fat: 0 } },
  ],
};

const removeIngredients = (opts) => ({
  id: "mg_remove",
  required: false,
  min: 0,
  max: 6,
  name: { tr: "Çıkarılacaklar", en: "Remove Ingredients", de: "Zutaten entfernen", fr: "Retirer des ingrédients", ru: "Убрать ингредиенты", ar: "إزالة مكونات" },
  options: opts,
});

const img = (u) => `${u}${u.includes("?") ? "&" : "?"}w=900&q=80`;

const mockProducts = [
  // ---------- KAHVALTI ----------
  {
    id: "p_serpme", categoryId: "cat_breakfast", basePrice: 420,
    name: { tr: "Serpme Kahvaltı (2 Kişilik)", en: "Grand Breakfast (for 2)", de: "Großes Frühstück (für 2)", fr: "Grand petit-déjeuner turc (pour 2)", ru: "Большой турецкий завтрак (на двоих)", ar: "فطور تركي كبير (لشخصين)" },
    description: { tr: "22 çeşit zengin kahvaltı tabağı, sınırsız çay ile.", en: "22-item rich breakfast platter with unlimited tea.", de: "22-teilige reichhaltige Frühstücksplatte mit unbegrenztem Tee.", fr: "Plateau de 22 spécialités avec thé à volonté.", ru: "Сет из 22 позиций с неограниченным чаем.", ar: "طبق فطور غني من 22 صنفًا مع شاي غير محدود." },
    image: img("https://images.unsplash.com/photo-1580069491658-8220b0e8722d"),
    popular: true, spicy: false, vegetarian: true, available: true, prepTimeMin: 20,
    allergens: ["gluten", "dairy", "egg"],
    ingredients: {
      tr: ["Beyaz peynir", "Zeytin", "Bal", "Kaymak", "Domates", "Salatalık", "Reçel"],
      en: ["Cheese", "Olives", "Honey", "Clotted cream", "Tomato", "Cucumber", "Jam"],
      de: ["Käse", "Oliven", "Honig", "Sahne", "Tomate", "Gurke", "Marmelade"],
      fr: ["Fromage blanc", "Olives", "Miel", "Crème épaisse", "Tomate", "Concombre", "Confiture"],
      ru: ["Белый сыр", "Оливки", "Мёд", "Каймак", "Помидор", "Огурец", "Варенье"],
      ar: ["جبن أبيض", "زيتون", "عسل", "قشطة", "طماطم", "خيار", "مربى"],
    },
    nutrition: { calories: 980, protein: 34, carbs: 72, fat: 58 },
    modifierGroups: [],
  },
  {
    id: "p_menemen", categoryId: "cat_breakfast", basePrice: 165,
    name: { tr: "Menemen", en: "Menemen", de: "Menemen", fr: "Menemen", ru: "Менемен", ar: "منمن" },
    description: { tr: "Domates, biber ve yumurta ile geleneksel kahvaltı klasiği.", en: "Traditional scramble with tomato, pepper and egg.", de: "Traditionelles Rührei mit Tomate, Paprika und Ei.", fr: "Œufs brouillés traditionnels à la tomate et au poivron.", ru: "Традиционная яичница с помидорами и перцем.", ar: "بيض مخفوق تقليدي بالطماطم والفلفل." },
    image: img("https://images.unsplash.com/photo-1614800443187-cc573044fa19"),
    popular: false, spicy: true, vegetarian: true, available: true, prepTimeMin: 12,
    allergens: ["egg", "dairy"],
    ingredients: {
      tr: ["Yumurta", "Domates", "Yeşil biber", "Tereyağı"],
      en: ["Egg", "Tomato", "Green pepper", "Butter"],
      de: ["Ei", "Tomate", "Grüne Paprika", "Butter"],
      fr: ["Œuf", "Tomate", "Poivron vert", "Beurre"],
      ru: ["Яйцо", "Помидор", "Зелёный перец", "Сливочное масло"],
      ar: ["بيض", "طماطم", "فلفل أخضر", "زبدة"],
    },
    nutrition: { calories: 320, protein: 18, carbs: 12, fat: 22 },
    modifierGroups: [
      { id: "mg_menemen", required: false, min: 0, max: 2, name: { tr: "İlave", en: "Add-on", de: "Zusatz", fr: "Supplément", ru: "Добавка", ar: "إضافة" }, options: [
        { id: "mn_sucuk", name: { tr: "Sucuk", en: "Sujuk", de: "Sucuk", fr: "Sucuk (saucisse épicée)", ru: "Суджук", ar: "سجق" }, priceDelta: 45, nutritionDelta: { calories: 150, protein: 8, carbs: 1, fat: 13 } },
        { id: "mn_cheese", name: { tr: "Kaşar Peyniri", en: "Cheddar", de: "Käse", fr: "Cheddar", ru: "Сыр чеддер", ar: "جبن شيدر" }, priceDelta: 30, nutritionDelta: { calories: 80, protein: 5, carbs: 1, fat: 6 }, allergensAdd: ["dairy"] },
      ]},
      removeIngredients([
        { id: "rm_mn_butter", name: { tr: "Tereyağsız (zeytinyağı ile)", en: "No butter (cooked in olive oil)", de: "Ohne Butter (mit Olivenöl)", fr: "Sans beurre (à l'huile d'olive)", ru: "Без сливочного масла (на оливковом)", ar: "بدون زبدة (بزيت الزيتون)" }, priceDelta: 0, nutritionDelta: { calories: -10, protein: 0, carbs: 0, fat: -1 }, allergensRemove: ["dairy"] },
        { id: "rm_mn_pepper", name: { tr: "Bibersiz", en: "No green pepper", de: "Ohne grüne Paprika", fr: "Sans poivron vert", ru: "Без зелёного перца", ar: "بدون فلفل أخضر" }, priceDelta: 0, nutritionDelta: { calories: -5, protein: 0, carbs: -1, fat: 0 } },
      ]),
    ],
  },
  {
    id: "p_simit_tabagi", categoryId: "cat_breakfast", basePrice: 95,
    name: { tr: "Simit & Peynir Tabağı", en: "Simit & Cheese Plate", de: "Simit & Käse Teller", fr: "Assiette simit & fromage", ru: "Симит с сыром", ar: "طبق سميت وجبن" },
    description: { tr: "Taze susamlı simit, beyaz peynir ve zeytin.", en: "Fresh sesame simit with cheese and olives.", de: "Frisches Sesam-Simit mit Käse und Oliven.", fr: "Simit frais au sésame, fromage et olives.", ru: "Свежий симит с кунжутом, сыр и оливки.", ar: "سميت طازج بالسمسم مع الجبن والزيتون." },
    image: img("https://images.unsplash.com/photo-1647772809798-f34d785c981c"),
    popular: false, spicy: false, vegetarian: true, available: true, prepTimeMin: 6,
    allergens: ["gluten", "dairy", "sesame"],
    ingredients: {
      tr: ["Simit", "Beyaz peynir", "Zeytin"],
      en: ["Simit", "Cheese", "Olives"],
      de: ["Simit", "Käse", "Oliven"],
      fr: ["Simit", "Fromage", "Olives"],
      ru: ["Симит", "Сыр", "Оливки"],
      ar: ["سميت", "جبن", "زيتون"],
    },
    nutrition: { calories: 410, protein: 15, carbs: 48, fat: 18 },
    modifierGroups: [],
  },
  {
    id: "p_pankek", categoryId: "cat_breakfast", basePrice: 145,
    name: { tr: "Bal & Muzlu Pankek", en: "Honey & Banana Pancakes", de: "Honig-Bananen-Pfannkuchen", fr: "Pancakes miel & banane", ru: "Панкейки с мёдом и бананом", ar: "بان كيك بالعسل والموز" },
    description: { tr: "Üç kat pankek, taze muz ve akışkan bal ile.", en: "Three fluffy pancakes with fresh banana and honey.", de: "Drei fluffige Pfannkuchen mit Banane und Honig.", fr: "Trois pancakes moelleux, banane fraîche et miel.", ru: "Три пышных панкейка со свежим бананом и мёдом.", ar: "ثلاث فطائر هشة مع موز طازج وعسل." },
    image: img("https://images.unsplash.com/photo-1661685452870-e89b6e8c14fa"),
    popular: true, spicy: false, vegetarian: true, available: false, prepTimeMin: 14,
    allergens: ["gluten", "dairy", "egg", "nuts"],
    ingredients: {
      tr: ["Pankek", "Muz", "Bal", "Fındık"],
      en: ["Pancake", "Banana", "Honey", "Hazelnut"],
      de: ["Pfannkuchen", "Banane", "Honig", "Haselnuss"],
      fr: ["Pancake", "Banane", "Miel", "Noisette"],
      ru: ["Панкейк", "Банан", "Мёд", "Фундук"],
      ar: ["بان كيك", "موز", "عسل", "بندق"],
    },
    nutrition: { calories: 560, protein: 12, carbs: 82, fat: 20 },
    modifierGroups: [],
  },

  // ---------- BURGERLER ----------
  {
    id: "p_fudi_burger", categoryId: "cat_burgers", basePrice: 245,
    name: { tr: "Fudi Signature Burger", en: "Fudi Signature Burger", de: "Fudi Signature Burger", fr: "Burger Signature Fudi", ru: "Фирменный бургер Fudi", ar: "برجر فودي المميز" },
    description: { tr: "180gr dana köfte, cheddar, karamelize soğan ve özel sos.", en: "180g beef patty, cheddar, caramelized onion and house sauce.", de: "180g Rindfleisch, Cheddar, Karamellzwiebel und Haussoße.", fr: "Steak de bœuf 180 g, cheddar, oignons caramélisés et sauce maison.", ru: "Говяжья котлета 180 г, чеддер, карамелизированный лук и фирменный соус.", ar: "قطعة لحم بقري 180 غ، جبن شيدر، بصل مكرمل وصلصة خاصة." },
    image: img("https://images.unsplash.com/photo-1610440042657-612c34d95e9f"),
    popular: true, spicy: false, vegetarian: false, available: true, prepTimeMin: 16,
    allergens: ["gluten", "dairy", "egg", "mustard"],
    ingredients: {
      tr: ["Dana köfte", "Cheddar", "Marul", "Domates", "Turşu", "Karamelize soğan", "Brioche ekmek"],
      en: ["Beef patty", "Cheddar", "Lettuce", "Tomato", "Pickles", "Caramelized onion", "Brioche bun"],
      de: ["Rindfleisch", "Cheddar", "Salat", "Tomate", "Gurken", "Karamellzwiebel", "Brioche"],
      fr: ["Steak de bœuf", "Cheddar", "Laitue", "Tomate", "Cornichons", "Oignons caramélisés", "Pain brioché"],
      ru: ["Говяжья котлета", "Чеддер", "Салат", "Помидор", "Маринованные огурцы", "Карамелизированный лук", "Булочка бриошь"],
      ar: ["لحم بقري", "جبن شيدر", "خس", "طماطم", "مخلل", "بصل مكرمل", "خبز بريوش"],
    },
    nutrition: { calories: 740, protein: 38, carbs: 46, fat: 44 },
    modifierGroups: [
      burgerExtras,
      sauceGroup,
      removeIngredients([
        { id: "rm_onion", name: { tr: "Soğansız", en: "No onion", de: "Ohne Zwiebel", fr: "Sans oignon", ru: "Без лука", ar: "بدون بصل" }, priceDelta: 0, nutritionDelta: { calories: -20, protein: 0, carbs: -4, fat: 0 } },
        { id: "rm_pickle", name: { tr: "Turşusuz", en: "No pickles", de: "Ohne Gurken", fr: "Sans cornichons", ru: "Без маринованных огурцов", ar: "بدون مخلل" }, priceDelta: 0, nutritionDelta: { calories: -5, protein: 0, carbs: -1, fat: 0 } },
        { id: "rm_tomato", name: { tr: "Domatessiz", en: "No tomato", de: "Ohne Tomate", fr: "Sans tomate", ru: "Без помидора", ar: "بدون طماطم" }, priceDelta: 0, nutritionDelta: { calories: -5, protein: 0, carbs: -1, fat: 0 } },
      ]),
    ],
  },
  {
    id: "p_double_burger", categoryId: "cat_burgers", basePrice: 310,
    name: { tr: "Double Smash Burger", en: "Double Smash Burger", de: "Double Smash Burger", fr: "Double Smash Burger", ru: "Двойной смэш-бургер", ar: "برجر سماش مزدوج" },
    description: { tr: "İki kat smash köfte, çift cheddar ve dana bacon.", en: "Two smash patties, double cheddar and beef bacon.", de: "Zwei Smash-Pattys, doppelter Cheddar und Rinderspeck.", fr: "Deux steaks smashés, double cheddar et bacon de bœuf.", ru: "Две смэш-котлеты, двойной чеддер и говяжий бекон.", ar: "قطعتا لحم سماش، جبن شيدر مزدوج ولحم بقري مقدد." },
    image: img("https://images.unsplash.com/photo-1561758033-d89a9ad46330"),
    popular: true, spicy: false, vegetarian: false, available: true, prepTimeMin: 17,
    allergens: ["gluten", "dairy", "egg"],
    ingredients: {
      tr: ["2x dana köfte", "Çift cheddar", "Dana bacon", "Soğan"],
      en: ["2x beef patty", "Double cheddar", "Beef bacon", "Onion"],
      de: ["2x Rindfleisch", "Doppel Cheddar", "Rinderspeck", "Zwiebel"],
      fr: ["2 steaks de bœuf", "Double cheddar", "Bacon de bœuf", "Oignon"],
      ru: ["2 говяжьи котлеты", "Двойной чеддер", "Говяжий бекон", "Лук"],
      ar: ["قطعتا لحم بقري", "جبن شيدر مزدوج", "لحم بقري مقدد", "بصل"],
    },
    nutrition: { calories: 980, protein: 52, carbs: 44, fat: 62 },
    modifierGroups: [burgerExtras, sauceGroup],
  },
  {
    id: "p_chicken_burger", categoryId: "cat_burgers", basePrice: 215,
    name: { tr: "Crispy Tavuk Burger", en: "Crispy Chicken Burger", de: "Crispy Hähnchen Burger", fr: "Burger poulet croustillant", ru: "Бургер с хрустящей курицей", ar: "برجر دجاج مقرمش" },
    description: { tr: "Çıtır tavuk göğsü, coleslaw ve acı mayonez.", en: "Crispy chicken breast, coleslaw and spicy mayo.", de: "Knuspriges Hähnchen, Coleslaw und scharfe Mayo.", fr: "Blanc de poulet croustillant, coleslaw et mayo épicée.", ru: "Хрустящая куриная грудка, коулслоу и острый майонез.", ar: "صدر دجاج مقرمش، سلطة كولسلو ومايونيز حار." },
    image: img("https://images.unsplash.com/photo-1594212699903-ec8a3eca50f5"),
    popular: false, spicy: true, vegetarian: false, available: true, prepTimeMin: 15,
    allergens: ["gluten", "egg", "mustard"],
    ingredients: {
      tr: ["Çıtır tavuk", "Coleslaw", "Acı mayonez", "Marul"],
      en: ["Crispy chicken", "Coleslaw", "Spicy mayo", "Lettuce"],
      de: ["Crispy Hähnchen", "Coleslaw", "Scharfe Mayo", "Salat"],
      fr: ["Poulet croustillant", "Coleslaw", "Mayo épicée", "Laitue"],
      ru: ["Хрустящая курица", "Коулслоу", "Острый майонез", "Салат"],
      ar: ["دجاج مقرمش", "كولسلو", "مايونيز حار", "خس"],
    },
    nutrition: { calories: 680, protein: 34, carbs: 52, fat: 36 },
    modifierGroups: [sauceGroup],
  },
  {
    id: "p_fries", categoryId: "cat_burgers", basePrice: 95,
    name: { tr: "Elma Dilim Patates", en: "Potato Wedges", de: "Kartoffelecken", fr: "Potatoes", ru: "Картофельные дольки", ar: "بطاطس ودجز" },
    description: { tr: "Baharatlı elma dilim patates, cheddar sos ile.", en: "Seasoned potato wedges with cheddar dip.", de: "Gewürzte Kartoffelecken mit Cheddar-Dip.", fr: "Quartiers de pommes de terre épicés, sauce cheddar.", ru: "Пряные картофельные дольки с соусом чеддер.", ar: "أصابع بطاطس متبلة مع صلصة الشيدر." },
    image: img("https://images.unsplash.com/photo-1630384060421-cb20d0e0649d"),
    popular: false, spicy: false, vegetarian: true, available: true, prepTimeMin: 10,
    allergens: ["dairy"],
    ingredients: {
      tr: ["Patates", "Baharat", "Cheddar sos"],
      en: ["Potato", "Spices", "Cheddar dip"],
      de: ["Kartoffel", "Gewürze", "Cheddar-Dip"],
      fr: ["Pomme de terre", "Épices", "Sauce cheddar"],
      ru: ["Картофель", "Специи", "Соус чеддер"],
      ar: ["بطاطس", "بهارات", "صلصة شيدر"],
    },
    nutrition: { calories: 420, protein: 8, carbs: 54, fat: 20 },
    modifierGroups: [sizeGroup([0, 20, 35])],
  },

  // ---------- PIZZALAR ----------
  {
    id: "p_margherita", categoryId: "cat_pizza", basePrice: 220,
    name: { tr: "Margherita", en: "Margherita", de: "Margherita", fr: "Margherita", ru: "Маргарита", ar: "مارغريتا" },
    description: { tr: "San Marzano domates, taze mozzarella ve fesleğen.", en: "San Marzano tomato, fresh mozzarella and basil.", de: "San-Marzano-Tomate, Mozzarella und Basilikum.", fr: "Tomate San Marzano, mozzarella fraîche et basilic.", ru: "Томаты Сан-Марцано, свежая моцарелла и базилик.", ar: "طماطم سان مارزانو، موزاريلا طازجة وريحان." },
    image: img("https://images.unsplash.com/photo-1579751626657-72bc17010498"),
    popular: true, spicy: false, vegetarian: true, available: true, prepTimeMin: 18,
    allergens: ["gluten", "dairy"],
    ingredients: {
      tr: ["Domates sos", "Mozzarella", "Fesleğen", "Zeytinyağı"],
      en: ["Tomato sauce", "Mozzarella", "Basil", "Olive oil"],
      de: ["Tomatensoße", "Mozzarella", "Basilikum", "Olivenöl"],
      fr: ["Sauce tomate", "Mozzarella", "Basilic", "Huile d'olive"],
      ru: ["Томатный соус", "Моцарелла", "Базилик", "Оливковое масло"],
      ar: ["صلصة طماطم", "موزاريلا", "ريحان", "زيت زيتون"],
    },
    nutrition: { calories: 820, protein: 32, carbs: 96, fat: 34 },
    modifierGroups: [sizeGroup([0, 60, 110])],
  },
  {
    id: "p_pepperoni", categoryId: "cat_pizza", basePrice: 265,
    name: { tr: "Pepperoni", en: "Pepperoni", de: "Pepperoni", fr: "Pepperoni", ru: "Пепперони", ar: "بيبروني" },
    description: { tr: "Bol pepperoni, mozzarella ve acı biber kırığı.", en: "Loaded pepperoni, mozzarella and chili flakes.", de: "Reichlich Pepperoni, Mozzarella und Chiliflocken.", fr: "Pepperoni généreux, mozzarella et piment en flocons.", ru: "Много пепперони, моцарелла и хлопья чили.", ar: "بيبروني وفير، موزاريلا ورقائق الفلفل الحار." },
    image: img("https://images.unsplash.com/photo-1622880833523-7cf1c0bd4296"),
    popular: true, spicy: true, vegetarian: false, available: true, prepTimeMin: 18,
    allergens: ["gluten", "dairy"],
    ingredients: {
      tr: ["Pepperoni", "Mozzarella", "Domates sos"],
      en: ["Pepperoni", "Mozzarella", "Tomato sauce"],
      de: ["Pepperoni", "Mozzarella", "Tomatensoße"],
      fr: ["Pepperoni", "Mozzarella", "Sauce tomate"],
      ru: ["Пепперони", "Моцарелла", "Томатный соус"],
      ar: ["بيبروني", "موزاريلا", "صلصة طماطم"],
    },
    nutrition: { calories: 960, protein: 40, carbs: 92, fat: 48 },
    modifierGroups: [sizeGroup([0, 60, 110]), sauceGroup],
  },
  {
    id: "p_quattro", categoryId: "cat_pizza", basePrice: 285,
    name: { tr: "Quattro Formaggi", en: "Quattro Formaggi", de: "Quattro Formaggi", fr: "Quattro Formaggi", ru: "Четыре сыра", ar: "أربعة أجبان" },
    description: { tr: "Dört peynirli: mozzarella, gorgonzola, parmesan, cheddar.", en: "Four cheeses: mozzarella, gorgonzola, parmesan, cheddar.", de: "Vier Käse: Mozzarella, Gorgonzola, Parmesan, Cheddar.", fr: "Quatre fromages : mozzarella, gorgonzola, parmesan, cheddar.", ru: "Четыре сыра: моцарелла, горгонзола, пармезан, чеддер.", ar: "أربعة أجبان: موزاريلا، غورغونزولا، بارميزان، شيدر." },
    image: img("https://images.unsplash.com/photo-1621538997517-58ce53933faa"),
    popular: false, spicy: false, vegetarian: true, available: true, prepTimeMin: 19,
    allergens: ["gluten", "dairy"],
    ingredients: {
      tr: ["Mozzarella", "Gorgonzola", "Parmesan", "Cheddar"],
      en: ["Mozzarella", "Gorgonzola", "Parmesan", "Cheddar"],
      de: ["Mozzarella", "Gorgonzola", "Parmesan", "Cheddar"],
      fr: ["Mozzarella", "Gorgonzola", "Parmesan", "Cheddar"],
      ru: ["Моцарелла", "Горгонзола", "Пармезан", "Чеддер"],
      ar: ["موزاريلا", "غورغونزولا", "بارميزان", "شيدر"],
    },
    nutrition: { calories: 1010, protein: 44, carbs: 88, fat: 56 },
    modifierGroups: [sizeGroup([0, 60, 110])],
  },

  // ---------- MAKARNALAR ----------
  {
    id: "p_pesto", categoryId: "cat_pasta", basePrice: 195,
    name: { tr: "Fesleğenli Pesto Makarna", en: "Basil Pesto Pasta", de: "Basilikum-Pesto-Pasta", fr: "Pâtes au pesto de basilic", ru: "Паста с песто из базилика", ar: "معكرونة بيستو بالريحان" },
    description: { tr: "Taze fesleğen pesto, cherry domates ve parmesan.", en: "Fresh basil pesto, cherry tomatoes and parmesan.", de: "Frisches Basilikumpesto, Kirschtomaten und Parmesan.", fr: "Pesto de basilic frais, tomates cerises et parmesan.", ru: "Свежее песто из базилика, черри и пармезан.", ar: "بيستو ريحان طازج، طماطم كرزية وبارميزان." },
    image: img("https://images.unsplash.com/photo-1473093295043-cdd812d0e601"),
    popular: true, spicy: false, vegetarian: true, available: true, prepTimeMin: 14,
    allergens: ["gluten", "dairy", "nuts"],
    ingredients: {
      tr: ["Farfalle", "Pesto", "Cherry domates", "Parmesan"],
      en: ["Farfalle", "Pesto", "Cherry tomato", "Parmesan"],
      de: ["Farfalle", "Pesto", "Kirschtomate", "Parmesan"],
      fr: ["Farfalle", "Pesto", "Tomate cerise", "Parmesan"],
      ru: ["Фарфалле", "Песто", "Помидоры черри", "Пармезан"],
      ar: ["فارفالي", "بيستو", "طماطم كرزية", "بارميزان"],
    },
    nutrition: { calories: 620, protein: 20, carbs: 82, fat: 24 },
    modifierGroups: [],
  },
  {
    id: "p_carbonara", categoryId: "cat_pasta", basePrice: 215,
    name: { tr: "Spaghetti Carbonara", en: "Spaghetti Carbonara", de: "Spaghetti Carbonara", fr: "Spaghetti carbonara", ru: "Спагетти карбонара", ar: "سباغيتي كاربونارا" },
    description: { tr: "Kremalı yumurta sosu, dana jambon ve parmesan.", en: "Creamy egg sauce, beef ham and parmesan.", de: "Cremige Eiersoße, Rinderschinken und Parmesan.", fr: "Sauce crémeuse aux œufs, jambon de bœuf et parmesan.", ru: "Сливочный яичный соус, говяжья ветчина и пармезан.", ar: "صلصة البيض الكريمية، لحم بقري مدخن وبارميزان." },
    image: img("https://images.unsplash.com/photo-1612874742237-6526221588e3"),
    popular: false, spicy: false, vegetarian: false, available: true, prepTimeMin: 15,
    allergens: ["gluten", "dairy", "egg"],
    ingredients: {
      tr: ["Spaghetti", "Yumurta", "Dana jambon", "Parmesan"],
      en: ["Spaghetti", "Egg", "Beef ham", "Parmesan"],
      de: ["Spaghetti", "Ei", "Rinderschinken", "Parmesan"],
      fr: ["Spaghetti", "Œuf", "Jambon de bœuf", "Parmesan"],
      ru: ["Спагетти", "Яйцо", "Говяжья ветчина", "Пармезан"],
      ar: ["سباغيتي", "بيض", "لحم بقري مدخن", "بارميزان"],
    },
    nutrition: { calories: 720, protein: 28, carbs: 78, fat: 34 },
    modifierGroups: [],
  },
  {
    id: "p_arrabiata", categoryId: "cat_pasta", basePrice: 185,
    name: { tr: "Penne Arrabiata", en: "Penne Arrabiata", de: "Penne Arrabiata", fr: "Penne all'arrabbiata", ru: "Пенне арраббьята", ar: "بيني أرابياتا" },
    description: { tr: "Acılı domates sos, sarımsak ve taze maydanoz.", en: "Spicy tomato sauce, garlic and fresh parsley.", de: "Scharfe Tomatensoße, Knoblauch und Petersilie.", fr: "Sauce tomate épicée, ail et persil frais.", ru: "Острый томатный соус, чеснок и свежая петрушка.", ar: "صلصة طماطم حارة، ثوم وبقدونس طازج." },
    image: img("https://images.unsplash.com/photo-1498579150354-977475b7ea0b"),
    popular: false, spicy: true, vegetarian: true, available: true, prepTimeMin: 13,
    allergens: ["gluten"],
    ingredients: {
      tr: ["Penne", "Domates", "Sarımsak", "Acı biber"],
      en: ["Penne", "Tomato", "Garlic", "Chili"],
      de: ["Penne", "Tomate", "Knoblauch", "Chili"],
      fr: ["Penne", "Tomate", "Ail", "Piment"],
      ru: ["Пенне", "Помидор", "Чеснок", "Перец чили"],
      ar: ["بيني", "طماطم", "ثوم", "فلفل حار"],
    },
    nutrition: { calories: 540, protein: 16, carbs: 88, fat: 14 },
    modifierGroups: [],
  },
  {
    id: "p_alfredo", categoryId: "cat_pasta", basePrice: 225,
    name: { tr: "Tavuklu Alfredo", en: "Chicken Alfredo", de: "Hähnchen Alfredo", fr: "Alfredo au poulet", ru: "Альфредо с курицей", ar: "ألفريدو بالدجاج" },
    description: { tr: "Kremalı alfredo sos, ızgara tavuk ve fettuccine.", en: "Creamy alfredo, grilled chicken and fettuccine.", de: "Cremige Alfredo, gegrilltes Hähnchen und Fettuccine.", fr: "Sauce alfredo crémeuse, poulet grillé et fettuccine.", ru: "Сливочный соус альфредо, курица гриль и феттучини.", ar: "صلصة ألفريدو كريمية، دجاج مشوي وفيتوتشيني." },
    image: img("https://images.unsplash.com/photo-1645112411341-6c4fd023714a"),
    popular: false, spicy: false, vegetarian: false, available: true, prepTimeMin: 16,
    allergens: ["gluten", "dairy"],
    ingredients: {
      tr: ["Fettuccine", "Tavuk", "Krema", "Parmesan"],
      en: ["Fettuccine", "Chicken", "Cream", "Parmesan"],
      de: ["Fettuccine", "Hähnchen", "Sahne", "Parmesan"],
      fr: ["Fettuccine", "Poulet", "Crème", "Parmesan"],
      ru: ["Феттучини", "Курица", "Сливки", "Пармезан"],
      ar: ["فيتوتشيني", "دجاج", "كريمة", "بارميزان"],
    },
    nutrition: { calories: 780, protein: 36, carbs: 74, fat: 36 },
    modifierGroups: [],
  },

  // ---------- SALATALAR ----------
  {
    id: "p_caesar", categoryId: "cat_salads", basePrice: 175,
    name: { tr: "Tavuklu Sezar Salata", en: "Chicken Caesar Salad", de: "Hähnchen-Caesar-Salat", fr: "Salade César au poulet", ru: "Салат «Цезарь» с курицей", ar: "سلطة سيزر بالدجاج" },
    description: { tr: "Marul, ızgara tavuk, kruton, parmesan ve sezar sos.", en: "Romaine, grilled chicken, croutons, parmesan, caesar.", de: "Römersalat, Hähnchen, Croutons, Parmesan, Caesar.", fr: "Romaine, poulet grillé, croûtons, parmesan, sauce César.", ru: "Романо, курица гриль, гренки, пармезан, соус «Цезарь».", ar: "خس روماني، دجاج مشوي، خبز محمص، بارميزان وصلصة سيزر." },
    image: img("https://images.unsplash.com/photo-1512621776951-a57141f2eefd"),
    popular: true, spicy: false, vegetarian: false, available: true, prepTimeMin: 10,
    allergens: ["gluten", "dairy", "egg", "fish"],
    ingredients: {
      tr: ["Marul", "Tavuk", "Kruton", "Parmesan"],
      en: ["Romaine", "Chicken", "Croutons", "Parmesan"],
      de: ["Römersalat", "Hähnchen", "Croutons", "Parmesan"],
      fr: ["Romaine", "Poulet", "Croûtons", "Parmesan"],
      ru: ["Романо", "Курица", "Гренки", "Пармезан"],
      ar: ["خس روماني", "دجاج", "خبز محمص", "بارميزان"],
    },
    nutrition: { calories: 420, protein: 30, carbs: 22, fat: 24 },
    modifierGroups: [],
  },
  {
    id: "p_quinoa", categoryId: "cat_salads", basePrice: 185,
    name: { tr: "Kinoalı Avokado Bowl", en: "Quinoa Avocado Bowl", de: "Quinoa-Avocado-Bowl", fr: "Bowl quinoa avocat", ru: "Боул с киноа и авокадо", ar: "وعاء الكينوا والأفوكادو" },
    description: { tr: "Kinoa, avokado, nohut ve mevsim yeşillikleri.", en: "Quinoa, avocado, chickpeas and seasonal greens.", de: "Quinoa, Avocado, Kichererbsen und Blattgemüse.", fr: "Quinoa, avocat, pois chiches et jeunes pousses de saison.", ru: "Киноа, авокадо, нут и сезонная зелень.", ar: "كينوا، أفوكادو، حمص وخضار موسمية." },
    image: img("https://images.unsplash.com/photo-1512621776951-a57141f2eefd"),
    popular: false, spicy: false, vegetarian: true, available: true, prepTimeMin: 9,
    allergens: [],
    ingredients: {
      tr: ["Kinoa", "Avokado", "Nohut", "Yeşillik"],
      en: ["Quinoa", "Avocado", "Chickpeas", "Greens"],
      de: ["Quinoa", "Avocado", "Kichererbsen", "Grün"],
      fr: ["Quinoa", "Avocat", "Pois chiches", "Verdure"],
      ru: ["Киноа", "Авокадо", "Нут", "Зелень"],
      ar: ["كينوا", "أفوكادو", "حمص", "خضار ورقية"],
    },
    nutrition: { calories: 480, protein: 18, carbs: 56, fat: 22 },
    modifierGroups: [],
  },
  {
    id: "p_mevsim", categoryId: "cat_salads", basePrice: 120,
    name: { tr: "Mevsim Salata", en: "Garden Salad", de: "Gartensalat", fr: "Salade du jardin", ru: "Сезонный салат", ar: "سلطة الموسم" },
    description: { tr: "Taze mevsim yeşillikleri ve nar ekşili sos.", en: "Fresh seasonal greens with pomegranate dressing.", de: "Frisches Blattgemüse mit Granatapfeldressing.", fr: "Légumes verts de saison, vinaigrette à la grenade.", ru: "Свежая сезонная зелень с гранатовой заправкой.", ar: "خضار موسمية طازجة مع صلصة دبس الرمان." },
    image: img("https://images.unsplash.com/photo-1607532941433-304659e8198a"),
    popular: false, spicy: false, vegetarian: true, available: true, prepTimeMin: 7,
    allergens: [],
    ingredients: {
      tr: ["Marul", "Domates", "Salatalık", "Havuç"],
      en: ["Lettuce", "Tomato", "Cucumber", "Carrot"],
      de: ["Salat", "Tomate", "Gurke", "Karotte"],
      fr: ["Laitue", "Tomate", "Concombre", "Carotte"],
      ru: ["Салат", "Помидор", "Огурец", "Морковь"],
      ar: ["خس", "طماطم", "خيار", "جزر"],
    },
    nutrition: { calories: 180, protein: 5, carbs: 22, fat: 8 },
    modifierGroups: [],
  },

  // ---------- İÇECEKLER ----------
  {
    id: "p_turk_kahve", categoryId: "cat_drinks", basePrice: 65,
    name: { tr: "Türk Kahvesi", en: "Turkish Coffee", de: "Türkischer Kaffee", fr: "Café turc", ru: "Кофе по-турецки", ar: "قهوة تركية" },
    description: { tr: "Közde pişmiş geleneksel Türk kahvesi, lokum ile.", en: "Traditional Turkish coffee with Turkish delight.", de: "Traditioneller türkischer Kaffee mit Lokum.", fr: "Café turc traditionnel servi avec un loukoum.", ru: "Традиционный турецкий кофе с рахат-лукумом.", ar: "قهوة تركية تقليدية مع راحة الحلقوم." },
    image: img("https://images.unsplash.com/photo-1558122104-355edad709f6"),
    popular: true, spicy: false, vegetarian: true, available: true, prepTimeMin: 6,
    allergens: [],
    ingredients: {
      tr: ["Türk kahvesi", "Lokum"],
      en: ["Turkish coffee", "Turkish delight"],
      de: ["Türkischer Kaffee", "Lokum"],
      fr: ["Café turc", "Loukoum"],
      ru: ["Турецкий кофе", "Рахат-лукум"],
      ar: ["قهوة تركية", "راحة الحلقوم"],
    },
    nutrition: { calories: 60, protein: 1, carbs: 12, fat: 1 },
    modifierGroups: [
      { id: "mg_sugar", required: true, min: 1, max: 1, name: { tr: "Şeker", en: "Sugar", de: "Zucker", fr: "Sucre", ru: "Сахар", ar: "السكر" }, options: [
        { id: "sg_none", name: { tr: "Sade", en: "No sugar", de: "Ohne Zucker", fr: "Sans sucre", ru: "Без сахара", ar: "بدون سكر" }, priceDelta: 0, default: true },
        // Approximate demo deltas: ~1 tsp (5 g) / ~2 tsp (10 g) added sugar at ~4 kcal per g.
        { id: "sg_med", name: { tr: "Orta", en: "Medium", de: "Mittel", fr: "Moyennement sucré", ru: "Средне сладкий", ar: "سكر متوسط" }, priceDelta: 0, nutritionDelta: { calories: 20, carbs: 5 } },
        { id: "sg_sweet", name: { tr: "Şekerli", en: "Sweet", de: "Süß", fr: "Sucré", ru: "Сладкий", ar: "حلو" }, priceDelta: 0, nutritionDelta: { calories: 40, carbs: 10 } },
      ]},
    ],
  },
  {
    id: "p_latte", categoryId: "cat_drinks", basePrice: 95,
    name: { tr: "Buzlu Latte", en: "Iced Latte", de: "Eiskaffee Latte", fr: "Latte glacé", ru: "Айс-латте", ar: "لاتيه مثلج" },
    description: { tr: "Espresso shot, soğuk süt ve bol buz.", en: "Espresso shot, cold milk and plenty of ice.", de: "Espresso-Shot, kalte Milch und viel Eis.", fr: "Un shot d'espresso, lait froid et beaucoup de glaçons.", ru: "Шот эспрессо, холодное молоко и много льда.", ar: "جرعة إسبريسو، حليب بارد وكثير من الثلج." },
    image: img("https://images.unsplash.com/photo-1461023058943-07fcbe16d735"),
    popular: true, spicy: false, vegetarian: true, available: true, prepTimeMin: 5,
    allergens: ["dairy"],
    ingredients: {
      tr: ["Espresso", "Süt", "Buz"],
      en: ["Espresso", "Milk", "Ice"],
      de: ["Espresso", "Milch", "Eis"],
      fr: ["Espresso", "Lait", "Glaçons"],
      ru: ["Эспрессо", "Молоко", "Лёд"],
      ar: ["إسبريسو", "حليب", "ثلج"],
    },
    nutrition: { calories: 140, protein: 7, carbs: 14, fat: 6 },
    modifierGroups: [sizeGroup([0, 15, 25])],
  },
  {
    id: "p_limonata", categoryId: "cat_drinks", basePrice: 70,
    name: { tr: "Ev Yapımı Limonata", en: "Homemade Lemonade", de: "Hausgemachte Limonade", fr: "Citronnade maison", ru: "Домашний лимонад", ar: "ليموناضة منزلية" },
    description: { tr: "Taze sıkılmış limon, nane ve soda.", en: "Freshly squeezed lemon, mint and soda.", de: "Frisch gepresste Zitrone, Minze und Soda.", fr: "Citron pressé, menthe et eau gazeuse.", ru: "Свежевыжатый лимон, мята и содовая.", ar: "ليمون معصور طازج، نعناع وصودا." },
    image: img("https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd"),
    popular: false, spicy: false, vegetarian: true, available: true, prepTimeMin: 4,
    allergens: [],
    ingredients: {
      tr: ["Limon", "Nane", "Soda"],
      en: ["Lemon", "Mint", "Soda"],
      de: ["Zitrone", "Minze", "Soda"],
      fr: ["Citron", "Menthe", "Eau gazeuse"],
      ru: ["Лимон", "Мята", "Содовая"],
      ar: ["ليمون", "نعناع", "صودا"],
    },
    nutrition: { calories: 110, protein: 0, carbs: 28, fat: 0 },
    modifierGroups: [],
  },
  {
    id: "p_ayran", categoryId: "cat_drinks", basePrice: 40,
    name: { tr: "Ayran", en: "Ayran", de: "Ayran", fr: "Ayran", ru: "Айран", ar: "عيران" },
    description: { tr: "Köpüklü geleneksel ayran.", en: "Frothy traditional yogurt drink.", de: "Schaumiges traditionelles Joghurtgetränk.", fr: "Boisson traditionnelle au yaourt, mousseuse.", ru: "Традиционный пенный йогуртовый напиток.", ar: "مشروب لبن تقليدي برغوة." },
    image: img("https://images.unsplash.com/photo-1626200419199-391ae4be7a41"),
    popular: false, spicy: false, vegetarian: true, available: true, prepTimeMin: 2,
    allergens: ["dairy"],
    ingredients: {
      tr: ["Yoğurt", "Su", "Tuz"],
      en: ["Yogurt", "Water", "Salt"],
      de: ["Joghurt", "Wasser", "Salz"],
      fr: ["Yaourt", "Eau", "Sel"],
      ru: ["Йогурт", "Вода", "Соль"],
      ar: ["لبن", "ماء", "ملح"],
    },
    nutrition: { calories: 90, protein: 5, carbs: 7, fat: 4 },
    modifierGroups: [],
  },

  // ---------- TATLILAR ----------
  {
    id: "p_baklava", categoryId: "cat_desserts", basePrice: 185,
    name: { tr: "Antep Fıstıklı Baklava (4 Dilim)", en: "Pistachio Baklava (4 pcs)", de: "Pistazien-Baklava (4 St.)", fr: "Baklava à la pistache (4 pièces)", ru: "Пахлава с фисташками (4 шт.)", ar: "بقلاوة بالفستق (4 قطع)" },
    description: { tr: "El açması yufka, bol Antep fıstığı ve şerbet.", en: "Hand-rolled filo, pistachio and syrup.", de: "Handgerollter Filoteig, Pistazien und Sirup.", fr: "Pâte filo roulée à la main, pistaches et sirop.", ru: "Тесто фило ручной раскатки, фисташки и сироп.", ar: "عجينة فيلو مفرودة يدويًا، فستق حلبي وقطر." },
    image: img("https://images.unsplash.com/photo-1778448806194-8dc4f71f5a4c"),
    popular: true, spicy: false, vegetarian: true, available: true, prepTimeMin: 5,
    allergens: ["gluten", "nuts", "dairy"],
    ingredients: {
      tr: ["Yufka", "Antep fıstığı", "Şerbet", "Tereyağı"],
      en: ["Filo", "Pistachio", "Syrup", "Butter"],
      de: ["Filo", "Pistazie", "Sirup", "Butter"],
      fr: ["Pâte filo", "Pistache", "Sirop", "Beurre"],
      ru: ["Тесто фило", "Фисташки", "Сироп", "Сливочное масло"],
      ar: ["عجينة فيلو", "فستق حلبي", "قطر", "زبدة"],
    },
    nutrition: { calories: 520, protein: 8, carbs: 62, fat: 28 },
    modifierGroups: [],
  },
  {
    id: "p_sufle", categoryId: "cat_desserts", basePrice: 165,
    name: { tr: "Sıcak Çikolatalı Sufle", en: "Warm Chocolate Soufflé", de: "Warmes Schokoladensoufflé", fr: "Soufflé chaud au chocolat", ru: "Тёплое шоколадное суфле", ar: "سوفليه الشوكولاتة الساخن" },
    description: { tr: "Akışkan çikolata dolgulu sufle, vanilyalı dondurma ile.", en: "Molten chocolate soufflé with vanilla ice cream.", de: "Schokoladensoufflé mit Vanilleeis.", fr: "Soufflé au cœur coulant, glace à la vanille.", ru: "Суфле с жидкой шоколадной начинкой и ванильным мороженым.", ar: "سوفليه بحشوة شوكولاتة سائلة مع آيس كريم الفانيليا." },
    image: img("https://images.unsplash.com/photo-1624353365286-3f8d62daad51"),
    popular: true, spicy: false, vegetarian: true, available: true, prepTimeMin: 14,
    allergens: ["gluten", "dairy", "egg"],
    ingredients: {
      tr: ["Çikolata", "Yumurta", "Dondurma"],
      en: ["Chocolate", "Egg", "Ice cream"],
      de: ["Schokolade", "Ei", "Eis"],
      fr: ["Chocolat", "Œuf", "Glace"],
      ru: ["Шоколад", "Яйцо", "Мороженое"],
      ar: ["شوكولاتة", "بيض", "آيس كريم"],
    },
    nutrition: { calories: 610, protein: 10, carbs: 68, fat: 34 },
    modifierGroups: [],
  },
  {
    id: "p_cheesecake", categoryId: "cat_desserts", basePrice: 155,
    name: { tr: "Frambuazlı Cheesecake", en: "Raspberry Cheesecake", de: "Himbeer-Cheesecake", fr: "Cheesecake à la framboise", ru: "Чизкейк с малиной", ar: "تشيز كيك بالتوت" },
    description: { tr: "San Sebastian tarzı cheesecake, frambuaz sos ile.", en: "San Sebastian style cheesecake with raspberry sauce.", de: "Cheesecake nach San-Sebastian-Art mit Himbeersoße.", fr: "Cheesecake façon San Sebastián, coulis de framboise.", ru: "Чизкейк в стиле Сан-Себастьян с малиновым соусом.", ar: "تشيز كيك على طريقة سان سيباستيان مع صلصة التوت." },
    image: img("https://images.unsplash.com/photo-1533134242443-d4fd215305ad"),
    popular: false, spicy: false, vegetarian: true, available: false, prepTimeMin: 5,
    allergens: ["gluten", "dairy", "egg"],
    ingredients: {
      tr: ["Krem peynir", "Frambuaz", "Bisküvi"],
      en: ["Cream cheese", "Raspberry", "Biscuit"],
      de: ["Frischkäse", "Himbeere", "Keks"],
      fr: ["Fromage frais", "Framboise", "Biscuit"],
      ru: ["Сливочный сыр", "Малина", "Печенье"],
      ar: ["جبن كريمي", "توت العليق", "بسكويت"],
    },
    nutrition: { calories: 480, protein: 9, carbs: 44, fat: 30 },
    modifierGroups: [],
  },
  {
    id: "p_sutlac", categoryId: "cat_desserts", basePrice: 95,
    name: { tr: "Fırın Sütlaç", en: "Baked Rice Pudding", de: "Gebackener Milchreis", fr: "Riz au lait au four", ru: "Запечённый рисовый пудинг", ar: "أرز بالحليب في الفرن" },
    description: { tr: "Geleneksel fırınlanmış sütlaç, tarçın ile.", en: "Traditional baked rice pudding with cinnamon.", de: "Traditioneller Milchreis mit Zimt.", fr: "Riz au lait traditionnel gratiné, à la cannelle.", ru: "Традиционный запечённый рисовый пудинг с корицей.", ar: "أرز بالحليب تقليدي مخبوز مع القرفة." },
    image: img("https://images.unsplash.com/photo-1488477181946-6428a0291777"),
    popular: false, spicy: false, vegetarian: true, available: true, prepTimeMin: 4,
    allergens: ["dairy"],
    ingredients: {
      tr: ["Süt", "Pirinç", "Şeker", "Tarçın"],
      en: ["Milk", "Rice", "Sugar", "Cinnamon"],
      de: ["Milch", "Reis", "Zucker", "Zimt"],
      fr: ["Lait", "Riz", "Sucre", "Cannelle"],
      ru: ["Молоко", "Рис", "Сахар", "Корица"],
      ar: ["حليب", "أرز", "سكر", "قرفة"],
    },
    nutrition: { calories: 280, protein: 7, carbs: 48, fat: 7 },
    modifierGroups: [],
  },
];

const useMock = !isApiMenuSource();
export const categories = useMock ? [...mockCategories] : [];
export const products = useMock ? [...mockProducts] : [];

// Replace the live menu in place (existing imports keep their references).
export function installMenu(menu) {
  categories.splice(0, categories.length, ...menu.categories);
  products.splice(0, products.length, ...menu.products);
}

export const allergenMeta = {
  gluten: { name: { tr: "Gluten", en: "Gluten", de: "Gluten", fr: "Gluten", ru: "Глютен", ar: "غلوتين" }, icon: "Wheat" },
  dairy: { name: { tr: "Süt Ürünü", en: "Dairy", de: "Milch", fr: "Produits laitiers", ru: "Молочные продукты", ar: "منتجات الألبان" }, icon: "Milk" },
  egg: { name: { tr: "Yumurta", en: "Egg", de: "Ei", fr: "Œuf", ru: "Яйцо", ar: "بيض" }, icon: "Egg" },
  nuts: { name: { tr: "Kuruyemiş", en: "Nuts", de: "Nüsse", fr: "Fruits à coque", ru: "Орехи", ar: "مكسرات" }, icon: "Nut" },
  fish: { name: { tr: "Balık", en: "Fish", de: "Fisch", fr: "Poisson", ru: "Рыба", ar: "سمك" }, icon: "Fish" },
  sesame: { name: { tr: "Susam", en: "Sesame", de: "Sesam", fr: "Sésame", ru: "Кунжут", ar: "سمسم" }, icon: "Wheat" },
  mustard: { name: { tr: "Hardal", en: "Mustard", de: "Senf", fr: "Moutarde", ru: "Горчица", ar: "خردل" }, icon: "Soup" },
};

export function getProductById(id) {
  return products.find((p) => p.id === id) || null;
}
export function getCategoryById(id) {
  return categories.find((c) => c.id === id) || null;
}
export function getProductsByCategory(categoryId) {
  return products.filter((p) => p.categoryId === categoryId);
}
export function getPopularProducts() {
  return products.filter((p) => p.popular && p.available);
}

// Featured products come from tenant config (featuredIds); falls back to popular.
export function getFeaturedProducts(featuredIds = []) {
  const ids = featuredIds.filter(Boolean);
  if (!ids.length) return getPopularProducts();
  return ids
    .map((id) => products.find((p) => p.id === id))
    .filter((p) => p && p.available);
}
